/**
 * Public (anon-role) Supabase key handling (SEC-7c, W02).
 *
 * The publishable key (`sb_publishable_...`) replaces the legacy anon JWT. It is
 * NOT a JWT: it goes on the `apikey` header and is never sent as a Bearer token
 * and never decoded. A signed-in user's session JWT stays the `Authorization`
 * header, which is how RLS and session refresh keep working unchanged.
 *
 * Pure and dependency free, so server routes, client bundles and the client
 * factories can all import it. This is the public counterpart of `./service-key`.
 *
 * Dual-run: the publishable key is preferred, the legacy anon key is used only
 * while the publishable one is absent. Never remove that fallback here.
 *
 * Header contract:
 *  - REST / Auth / Storage: a publishable key goes on `apikey` only. A legacy
 *    anon key keeps `apikey` plus `Authorization: Bearer`, as before.
 *  - Edge Functions that still run with verify_jwt=true: the platform only
 *    understands JWTs, so a publishable key is never sent as Bearer. The legacy
 *    anon JWT stays on Bearer for as long as it is configured, with the
 *    publishable key added on `apikey`.
 */

const PUBLISHABLE_KEY_PREFIX = 'sb_publishable_';
const SECRET_KEY_PREFIX = 'sb_secret_';
const CREDENTIAL_HEADERS = new Set(['apikey', 'authorization']);

export function isPublishableKey(key: string | undefined): key is string {
  return typeof key === 'string' && key.startsWith(PUBLISHABLE_KEY_PREFIX);
}

/** The trimmed value, or `undefined` for anything that is not a non-blank string. */
export function cleanKey(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Like {@link cleanKey}, but a server-only secret key (`sb_secret_...`) is treated
 * as missing: it must never become a public key, travel in a header the browser
 * can see, or be inlined into a bundle. The value is never reported.
 */
export function cleanPublicKey(value: unknown): string | undefined {
  const cleaned = cleanKey(value);
  return cleaned !== undefined && !cleaned.startsWith(SECRET_KEY_PREFIX) ? cleaned : undefined;
}

function firstClean(values: readonly unknown[]): string | undefined {
  for (const value of values) {
    const cleaned = cleanPublicKey(value);
    if (cleaned !== undefined) return cleaned;
  }
  return undefined;
}

/** Publishable candidates first, then anon candidates. Blank values count as missing. */
export function pickPublicKey(
  publishable: readonly unknown[],
  anon: readonly unknown[],
): string | undefined {
  return firstClean(publishable) ?? firstClean(anon);
}

/**
 * `fetch` that drops the SDK's same-key `Authorization: Bearer <publishable key>`
 * fallback (a publishable key is not a JWT). A caller's own JWT is left alone.
 */
export function publicKeyAwareFetch(key: string, base?: typeof fetch): typeof fetch {
  if (!isPublishableKey(key)) return base ?? fetch;
  const wrapped = (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
    // Resolve the platform fetch per call, so test doubles and polyfills installed later still apply.
    const send = base ?? fetch;
    // Headers given in `init` replace a Request's own; otherwise the Request's headers apply.
    const source = init?.headers ?? (input instanceof Request ? input.headers : undefined);
    const headers = new Headers(source);
    if (headers.get('authorization') !== `Bearer ${key}`) return send(input, init);
    headers.delete('authorization');
    return send(input, { ...init, headers });
  };
  return wrapped as typeof fetch;
}

/**
 * `{ global: { fetch } }` to spread into a client options literal (keeps the
 * literal's own types, unlike {@link publicClientOptions}). Empty for a legacy
 * key, so legacy clients are built exactly as before.
 */
export function publicFetchOption(key: string): { global?: { fetch: typeof fetch } } {
  return isPublishableKey(key) ? { global: { fetch: publicKeyAwareFetch(key) } } : {};
}

export interface PublicClientOptions {
  auth?: Record<string, unknown>;
  global?: { fetch?: typeof fetch; headers?: Record<string, string> } & Record<string, unknown>;
  [option: string]: unknown;
}

/**
 * Options for `createClient(url, key, options)` with a public key. A legacy key
 * returns `base` untouched; a publishable key adds the Bearer-stripping fetch.
 */
export function publicClientOptions<T extends PublicClientOptions = PublicClientOptions>(
  key: string,
  base?: T,
): T {
  if (!isPublishableKey(key)) return (base ?? {}) as T;
  return {
    ...(base ?? {}),
    global: { ...(base?.global ?? {}), fetch: publicKeyAwareFetch(key, base?.global?.fetch) },
  } as T;
}

/** Headers for a raw REST / RPC request made with the public key. */
export function publicRestHeaders(key: string): Record<string, string> {
  return isPublishableKey(key)
    ? { apikey: key }
    : { apikey: key, Authorization: `Bearer ${key}` };
}

export interface PublicKeyCandidates {
  readonly publishable?: string | undefined;
  readonly anon?: string | undefined;
}

/**
 * Headers for calling a Supabase Edge Function with the public key.
 *
 * `extra` headers are kept but can never override the credential headers.
 * Returns no credential headers when no key is configured; the caller decides
 * how to fail.
 */
export function publicFunctionHeaders(
  keys: PublicKeyCandidates,
  extra: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const publishable = cleanPublicKey(keys.publishable);
  const anon = cleanPublicKey(keys.anon);
  const jwt = anon !== undefined && !isPublishableKey(anon) ? anon : undefined;

  const credentials: Record<string, string> = {};
  const apikey = publishable ?? anon;
  if (apikey) credentials.apikey = apikey;
  if (jwt) credentials.Authorization = `Bearer ${jwt}`;

  const passthrough = Object.fromEntries(
    Object.entries(extra).filter(([name]) => !CREDENTIAL_HEADERS.has(name.toLowerCase())),
  );
  return { ...passthrough, ...credentials };
}
